import axios from 'axios';
import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Typography, Card, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Select, FormControl, InputLabel, Chip,
  CircularProgress, TextField, InputAdornment, Avatar, Tabs, Tab, IconButton,
} from "@mui/material";
import { getAssessments, assignAssessment, getAssignedAssessments } from '../../service/customerassessment';
import Navbar from '../navbar';
import { getAssessmentsByCategory } from '../Assessments/questionnaires';
import QUESTIONNAIRES from '../Assessments/questionnaires';
import { resolveKey } from '../Assessments/questionnaires';
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import BarChartIcon from "@mui/icons-material/BarChart";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

// ─── Library helpers ─────────────────────────────────────────────────────────
const libCategoryStyle = {
  anxiety:    { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa", label: "Anxiety" },
  depression: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe", label: "Depression" },
  other:      { bg: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe", label: "Other" },
};
const libSeverityColor = {
  green:  { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
  yellow: { bg: "#fefce8", color: "#a16207", border: "#fef08a" },
  orange: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
  red:    { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
};

const LibQuestionRow = ({ q, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ border: "1px solid #e9ecef", borderRadius: "10px", overflow: "hidden" }}>
      <Box onClick={() => setOpen((p) => !p)}
        sx={{ px: 2, py: 1.25, display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer", bgcolor: open ? "#f8fafc" : "#fff", "&:hover": { bgcolor: "#f8fafc" } }}>
        <Box sx={{ width: 22, height: 22, borderRadius: "6px", bgcolor: "#e0e7ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#4338ca" }}>{index + 1}</Typography>
        </Box>
        <Typography sx={{ flex: 1, fontSize: "0.82rem", color: "#1f2937", fontWeight: 500 }}>{q.text}</Typography>
        <IconButton size="small" sx={{ p: 0, color: "#9ca3af" }}>
          {open ? <ExpandLessIcon sx={{ fontSize: "1rem" }} /> : <ExpandMoreIcon sx={{ fontSize: "1rem" }} />}
        </IconButton>
      </Box>
      {open && (
        <Box sx={{ px: 2, pb: 1.5, pt: 0.5, bgcolor: "#fafafa", borderTop: "1px solid #f1f5f9" }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
            {q.options.map((opt, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.6, px: 1.25, py: 0.5, borderRadius: "8px", bgcolor: "#fff", border: "1px solid #e2e8f0" }}>
                <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: "#e0e7ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "#4338ca" }}>{opt.score}</Typography>
                </Box>
                <Typography sx={{ fontSize: "0.75rem", color: "#374151" }}>{opt.text}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const LibDetailDialog = ({ open, onClose, q }) => {
  if (!q) return null;
  const cs = libCategoryStyle[q.category] || libCategoryStyle.other;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden", maxHeight: "90vh" } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ px: 3, pt: 2.5, pb: 2, bgcolor: cs.bg, borderBottom: "1px solid " + cs.border, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box sx={{ fontSize: "2rem", lineHeight: 1 }}>{q.icon}</Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#111827" }}>{q.name}</Typography>
              <Chip label={q.shortName} size="small" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, bgcolor: cs.color, color: "#fff", "& .MuiChip-label": { px: 0.875 } }} />
            </Box>
            <Typography sx={{ fontSize: "0.78rem", color: "#6b7280", mt: 0.3 }}>{q.description}</Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 0.75, flexWrap: "wrap" }}>
              <Chip label={q.questions.length + " questions"} size="small" sx={{ height: 20, fontSize: "0.63rem", bgcolor: "#fff", color: "#475569", border: "1px solid #e2e8f0", "& .MuiChip-label": { px: 0.75 } }} />
              <Chip label={"Max score: " + q.maxScore} size="small" sx={{ height: 20, fontSize: "0.63rem", bgcolor: "#fff", color: "#475569", border: "1px solid #e2e8f0", "& .MuiChip-label": { px: 0.75 } }} />
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: "#6b7280", mt: -0.5, flexShrink: 0 }}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {q.instructions && (
          <Box sx={{ px: 3, py: 1.75, bgcolor: "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.4 }}>Instructions</Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "#374151", fontStyle: "italic" }}>{q.instructions}</Typography>
          </Box>
        )}
        <Box sx={{ px: 3, py: 1.75, borderBottom: "1px solid #f1f5f9" }}>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>Scoring Guide</Typography>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            {q.scoring.map((s, i) => {
              const sc = libSeverityColor[s.color] || libSeverityColor.orange;
              return (
                <Box key={i} sx={{ px: 1.25, py: 0.75, borderRadius: "8px", bgcolor: sc.bg, border: "1px solid " + sc.border, minWidth: 110 }}>
                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: sc.color, textTransform: "uppercase", letterSpacing: "0.04em" }}>{s.level}</Typography>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: sc.color }}>{s.min}–{s.max}</Typography>
                  <Typography sx={{ fontSize: "0.65rem", color: sc.color + "bb", mt: 0.25 }}>{s.recommendation}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1.25 }}>Questions ({q.questions.length})</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {q.questions.map((question, i) => <LibQuestionRow key={question.id} q={question} index={i} />)}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const LibraryView = () => {
  const [libSearch, setLibSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const { anxiety, depression, other } = getAssessmentsByCategory();
  const allEntries = useMemo(() => [
    ...anxiety.map(([k, v]) => ({ key: k, q: v })),
    ...depression.map(([k, v]) => ({ key: k, q: v })),
    ...other.map(([k, v]) => ({ key: k, q: v })),
  ], []);
  const filtered = useMemo(() => {
    if (!libSearch.trim()) return allEntries;
    const s = libSearch.toLowerCase();
    return allEntries.filter((e) =>
      e.q.name.toLowerCase().includes(s) || e.q.shortName?.toLowerCase().includes(s) ||
      e.q.category.toLowerCase().includes(s)
    );
  }, [allEntries, libSearch]);
  const groups = [
    { label: "Anxiety",    items: filtered.filter((e) => e.q.category === "anxiety") },
    { label: "Depression", items: filtered.filter((e) => e.q.category === "depression") },
    { label: "Other",      items: filtered.filter((e) => e.q.category === "other") },
  ];
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827" }}>Assessment Library</Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{allEntries.length} validated clinical assessments — click to view questions and scoring</Typography>
        </Box>
        <TextField variant="outlined" size="small" placeholder="Search assessments..."
          value={libSearch} onChange={(e) => setLibSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#9ca3af", fontSize: "1rem" }} /></InputAdornment>,
            sx: { borderRadius: "10px", bgcolor: "#fff", fontSize: "0.82rem", "& fieldset": { borderColor: "#e2e8f0" }, "&:hover fieldset": { borderColor: "#cbd5e1" }, "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: "1.5px" } } }}
          sx={{ width: 250 }} />
      </Box>
      {groups.map((group) => group.items.length > 0 && (
        <Box key={group.label}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: (libCategoryStyle[group.label.toLowerCase()] || libCategoryStyle.other).color }}>{group.label}</Typography>
            <Box sx={{ flex: 1, height: "1px", bgcolor: "#e5e7eb" }} />
            <Chip label={group.items.length} size="small" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 600, bgcolor: "#f1f5f9", color: "#475569", "& .MuiChip-label": { px: 0.875 } }} />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" }, gap: 1.5 }}>
            {group.items.map((e) => {
              const cs = libCategoryStyle[e.q.category] || libCategoryStyle.other;
              return (
                <Card key={e.key} onClick={() => setSelected(e.q)}
                  sx={{ p: 2, borderRadius: "12px", border: "1px solid #e5e7eb", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", bgcolor: "#fff", cursor: "pointer", transition: "all 0.15s", "&:hover": { boxShadow: "0 4px 14px rgba(0,0,0,0.1)", borderColor: cs.border, transform: "translateY(-1px)" } }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: "10px", bgcolor: cs.bg, border: "1px solid " + cs.border, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.25rem", flexShrink: 0 }}>{e.q.icon}</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#111827", lineHeight: 1.35 }}>{e.q.name}</Typography>
                      <Typography sx={{ fontSize: "0.7rem", color: "#6b7280", fontWeight: 600 }}>{e.q.shortName}</Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: "0.72rem", color: "#6b7280", lineHeight: 1.5, mb: 1.25 }}>{e.q.description}</Typography>
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                    <Chip label={e.q.questions.length + " Qs"} size="small" sx={{ height: 18, fontSize: "0.6rem", fontWeight: 600, bgcolor: "#f1f5f9", color: "#475569", "& .MuiChip-label": { px: 0.6 } }} />
                    <Chip label={"Max " + e.q.maxScore} size="small" sx={{ height: 18, fontSize: "0.6rem", fontWeight: 600, bgcolor: "#f1f5f9", color: "#475569", "& .MuiChip-label": { px: 0.6 } }} />
                    <Chip label={cs.label} size="small" sx={{ height: 18, fontSize: "0.6rem", fontWeight: 600, bgcolor: cs.bg, color: cs.color, border: "1px solid " + cs.border, "& .MuiChip-label": { px: 0.6 } }} />
                  </Box>
                </Card>
              );
            })}
          </Box>
        </Box>
      ))}
      <LibDetailDialog open={!!selected} onClose={() => setSelected(null)} q={selected} />
    </Box>
  );
};

// ─── Score severity lookup ─────────────────────────────────────────────────────
const severityColorMap = {
  green:  { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
  yellow: { bg: "#fefce8", color: "#a16207", border: "#fef08a" },
  orange: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
  red:    { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
};

const getScoreSeverity = (assessmentKey, score) => {
  if (score == null || !assessmentKey) return null;
  const qData = QUESTIONNAIRES[resolveKey(assessmentKey)];
  if (!qData?.scoring) return null;
  const range = qData.scoring.find((s) => score >= s.min && score <= s.max);
  if (!range) return null;
  return { ...range, ...( severityColorMap[range.color] || severityColorMap.orange ) };
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getCategoryIcon = (name = "") => {
  const lower = name.toLowerCase();
  if (lower.includes("anxiety") || lower.includes("gad") || lower.includes("ham-a") ||
    lower.includes("stai") || lower.includes("worry") || lower.includes("phobia") ||
    lower.includes("spin") || lower.includes("sas")) return "🧠";
  if (lower.includes("depression") || lower.includes("phq") || lower.includes("bdi") ||
    lower.includes("ham-d") || lower.includes("madrs") || lower.includes("ces-d") ||
    lower.includes("geriatric")) return "😔";
  return "🧩";
};

const getCategory = (name = "") => {
  const lower = name.toLowerCase();
  if (lower.includes("anxiety") || lower.includes("gad") || lower.includes("ham-a") ||
    lower.includes("stai") || lower.includes("worry") || lower.includes("phobia") ||
    lower.includes("spin") || lower.includes("sas")) return "anxiety";
  if (lower.includes("depression") || lower.includes("phq") || lower.includes("bdi") ||
    lower.includes("ham-d") || lower.includes("madrs") || lower.includes("ces-d") ||
    lower.includes("geriatric")) return "depression";
  return "other";
};

const categoryLabels = { anxiety: "── Anxiety ──", depression: "── Depression ──", other: "── Other ──" };

const categoryStyle = {
  depression: { bg: "#dbeafe", color: "#1e40af", border: "#bfdbfe" },
  anxiety:    { bg: "#fef3c7", color: "#92400e", border: "#fde68a" },
  other:      { bg: "#f3e8ff", color: "#6b21a8", border: "#e9d5ff" },
};

const avatarPalette = (name = "") => {
  const palettes = [
    { bg: "#d1fae5", color: "#065f46" }, { bg: "#dbeafe", color: "#1e40af" },
    { bg: "#fef3c7", color: "#92400e" }, { bg: "#fce7f3", color: "#9d174d" },
    { bg: "#ede9fe", color: "#5b21b6" },
  ];
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) % palettes.length;
  return palettes[h];
};

// ─── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ label, count, color, bg, icon }) => (
  <Box sx={{ flex: "1 1 140px", p: 2, borderRadius: "14px", bgcolor: bg, border: "1px solid", borderColor: color + "33", display: "flex", alignItems: "center", gap: 1.75 }}>
    <Box sx={{ width: 40, height: 40, borderRadius: "10px", bgcolor: color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: color }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: color, lineHeight: 1 }}>{count}</Typography>
      <Typography sx={{ fontSize: "0.72rem", color: color + "cc", fontWeight: 500, mt: 0.25 }}>{label}</Typography>
    </Box>
  </Box>
);

// ─── Assessment Card ───────────────────────────────────────────────────────────
const AssessmentCard = ({ a, locked }) => {
  const catStyle = categoryStyle[a.category] || categoryStyle.other;
  const pal      = avatarPalette(a.patientName);
  const isDone   = a.status === "completed";
  const [copied, setCopied] = useState(false);
  const [showResponses, setShowResponses] = useState(false);
  const [responses, setResponses] = useState(null);
  const [loadingResponses, setLoadingResponses] = useState(false);

  const handleViewResponses = async () => {
    setShowResponses(true);
    if (responses) return;
    setLoadingResponses(true);
    try {
      const res = await axios.get(`/api/v1/customer-assessments/${a.customerId}/${a.assessmentKey}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setResponses(res.data);
    } catch {
      setResponses(null);
    } finally {
      setLoadingResponses(false);
    }
  };

  return (
    <>
    <Card sx={{ p: 2, borderRadius: "12px", border: "1px solid #e9ecef", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", bgcolor: "#fff", "&:hover": { boxShadow: "0 3px 10px rgba(0,0,0,0.08)", borderColor: "#d1d5db" }, transition: "all 0.15s" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.75 }}>
        <Box sx={{ width: 44, height: 44, borderRadius: "11px", bgcolor: catStyle.bg, border: "1px solid " + catStyle.border, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem", flexShrink: 0 }}>
          {a.icon}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#111827", lineHeight: 1.35 }}>
              {a.assessmentName}
            </Typography>
            {isDone ? (
              <Chip label="Completed" size="small"
                icon={<CheckCircleIcon sx={{ fontSize: "0.8rem !important", color: "#15803d !important" }} />}
                sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700, bgcolor: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", "& .MuiChip-label": { px: 0.75 }, flexShrink: 0 }} />
            ) : (
              <Chip label="Pending" size="small"
                icon={<PendingActionsIcon sx={{ fontSize: "0.8rem !important", color: "#b45309 !important" }} />}
                sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700, bgcolor: "#fffbeb", color: "#b45309", border: "1px solid #fde68a", "& .MuiChip-label": { px: 0.75 }, flexShrink: 0 }} />
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75, flexWrap: "wrap" }}>
            <Avatar sx={{ width: 20, height: 20, fontSize: "0.6rem", fontWeight: 700, bgcolor: pal.bg, color: pal.color }}>
              {a.patientName.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
            </Avatar>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151" }}>{a.patientName}</Typography>
            <Box sx={{ fontFamily: "monospace", fontSize: "0.68rem", color: "#9ca3af", bgcolor: "#f3f4f6", px: 0.6, py: 0.15, borderRadius: "4px" }}>#{a.customerId}</Box>
            <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "#d1d5db" }} />
            <Chip label={a.category} size="small" sx={{ height: 18, fontSize: "0.63rem", fontWeight: 600, bgcolor: catStyle.bg, color: catStyle.color, border: "1px solid " + catStyle.border, textTransform: "capitalize", "& .MuiChip-label": { px: 0.75 } }} />
          </Box>

          {/* Score / Value / Label – only when completed and score is available */}
          {!locked && isDone && a.obtainedScore != null && (() => {
            const sev = getScoreSeverity(a.assessmentKey, a.obtainedScore);
            const sc = sev || { bg: "#f8fafc", color: "#374151", border: "#e5e7eb" };
            return (
              <Box sx={{ mt: 1, borderRadius: "10px", border: "1px solid " + sc.border, overflow: "hidden" }}>
                <Box sx={{ display: "flex", divideX: 1 }}>
                  {/* Score */}
                  <Box sx={{ flex: 1, py: 1, px: 1.25, bgcolor: sc.bg, borderRight: "1px solid " + sc.border, textAlign: "center" }}>
                    <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: sc.color, textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.25 }}>Score</Typography>
                    <Typography sx={{ fontSize: "0.92rem", fontWeight: 800, color: sc.color, lineHeight: 1 }}>
                      {a.obtainedScore}
                      <Box component="span" sx={{ fontSize: "0.62rem", fontWeight: 400, color: sc.color + "99" }}>/{a.totalScore}</Box>
                    </Typography>
                  </Box>
                  {/* Value (range) */}
                  <Box sx={{ flex: 1, py: 1, px: 1.25, bgcolor: sc.bg, borderRight: "1px solid " + sc.border, textAlign: "center" }}>
                    <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: sc.color, textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.25 }}>Value</Typography>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: sc.color, lineHeight: 1 }}>
                      {sev ? `${sev.min}–${sev.max}` : "—"}
                    </Typography>
                  </Box>
                  {/* Label */}
                  <Box sx={{ flex: 2, py: 1, px: 1.25, bgcolor: sc.bg, textAlign: "center" }}>
                    <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, color: sc.color, textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.25 }}>Label</Typography>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: sc.color, lineHeight: 1.2 }}>
                      {sev ? sev.level : "—"}
                    </Typography>
                    {sev && (
                      <Typography sx={{ fontSize: "0.62rem", color: sc.color + "cc", fontStyle: "italic", mt: 0.2, lineHeight: 1.2 }}>
                        {sev.recommendation}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </Box>
      </Box>

      <Box sx={{ mt: 1.5, pt: 1.25, borderTop: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af" }}>Assigned: {a.date}</Typography>
        {isDone && !locked ? (
          <Box
            onClick={handleViewResponses}
            sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", "&:hover": { opacity: 0.75 } }}
          >
            <BarChartIcon sx={{ fontSize: "0.8rem", color: "#6b7280" }} />
            <Typography sx={{ fontSize: "0.7rem", color: "#3b82f6", fontWeight: 600 }}>
              View responses
            </Typography>
          </Box>
        ) : !isDone && !locked ? (
          <Box
            onClick={() => {
              const url = window.location.origin + "/assessment/" + a.customerId + "/" + a.assessmentKey;
              const copyText = (text) => {
                if (navigator.clipboard && window.isSecureContext) {
                  return navigator.clipboard.writeText(text);
                }
                // Fallback for HTTP
                const el = document.createElement("textarea");
                el.value = text;
                el.style.position = "fixed";
                el.style.opacity = "0";
                document.body.appendChild(el);
                el.focus();
                el.select();
                document.execCommand("copy");
                document.body.removeChild(el);
                return Promise.resolve();
              };
              copyText(url).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }).catch(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              });
            }}
            sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", color: copied ? "#22c55e" : "#3b82f6", "&:hover": { color: copied ? "#22c55e" : "#2563eb" }, transition: "color 0.2s" }}
          >
            <ContentCopyIcon sx={{ fontSize: "0.85rem" }} />
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
              {copied ? "Copied!" : "Copy Link"}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Card>

    {/* ── Responses Dialog ── */}
    <Dialog open={showResponses} onClose={() => setShowResponses(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1, borderBottom: "1px solid #f3f4f6" }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#111827" }}>{a.assessmentName}</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "#6b7280" }}>{a.patientName}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {responses && (() => {
            const score = responses.obtained_score ?? a.obtainedScore;
            const total = responses.total_score ?? a.totalScore;
            const sev   = getScoreSeverity(a.assessmentKey, score);
            return (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                <Chip label={`Score: ${score} / ${total}`}
                  size="small" sx={{ fontWeight: 700, fontSize: "0.78rem", bgcolor: "#eff6ff", color: "#3b82f6", border: "1px solid #bfdbfe" }} />
                {sev && (
                  <Box sx={{ px: 1, py: 0.3, borderRadius: "6px", bgcolor: sev.bg, border: "1px solid " + sev.border }}>
                    <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: sev.color }}>{sev.level} · {sev.recommendation}</Typography>
                  </Box>
                )}
              </Box>
            );
          })()}
          <Box onClick={() => setShowResponses(false)} sx={{ cursor: "pointer", color: "#9ca3af", "&:hover": { color: "#374151" } }}>
            <CloseIcon fontSize="small" />
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, maxHeight: "70vh", overflowY: "auto" }}>
        {loadingResponses ? (
          <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}><CircularProgress size={28} /></Box>
        ) : !responses ? (
          <Box sx={{ py: 5, textAlign: "center" }}>
            <Typography sx={{ color: "#6b7280", fontSize: "0.88rem" }}>No submission data found.</Typography>
          </Box>
        ) : (() => {
          const qData = QUESTIONNAIRES[resolveKey(a.assessmentKey)];
          if (!qData) return (
            <Box sx={{ py: 5, textAlign: "center" }}>
              <Typography sx={{ color: "#6b7280", fontSize: "0.88rem" }}>Questionnaire definition not found.</Typography>
            </Box>
          );
          const ans = responses.answers || {};
          return (
            <Box sx={{ px: 3, py: 2 }}>
              {qData.questions.map((q, idx) => {
                const selectedIdx = Number(ans[String(q.id)] ?? ans[q.id] ?? -1);
                const selectedOpt = selectedIdx >= 0 && selectedIdx < q.options.length ? q.options[selectedIdx] : undefined;
                return (
                  <Box key={q.id} sx={{ mb: 2.5, pb: 2.5, borderBottom: idx < qData.questions.length - 1 ? "1px solid #f3f4f6" : "none" }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#111827", mb: 1 }}>
                      <Box component="span" sx={{ color: "#3b82f6", mr: 0.75 }}>{idx + 1}.</Box>{q.text}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {q.options.map((opt, optIdx) => {
                        const isSelected = optIdx === selectedIdx;
                        return (
                          <Box key={optIdx} sx={{
                            px: 1.5, py: 0.6, borderRadius: "8px", fontSize: "0.78rem", fontWeight: isSelected ? 700 : 400,
                            bgcolor: isSelected ? "#eff6ff" : "#f8fafc",
                            color: isSelected ? "#1d4ed8" : "#6b7280",
                            border: isSelected ? "1.5px solid #3b82f6" : "1px solid #e5e7eb",
                          }}>
                            {opt.text}{isSelected && <Box component="span" sx={{ ml: 0.5 }}>✓</Box>}
                          </Box>
                        );
                      })}
                    </Box>
                    {selectedOpt === undefined && (
                      <Typography sx={{ fontSize: "0.72rem", color: "#f59e0b", mt: 0.5 }}>No answer recorded</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          );
        })()}
      </DialogContent>
    </Dialog>
  </>
  );
};

const EmptyState = ({ message, onAssign }) => (
  <Box sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.25 }}>
    <Box sx={{ width: 56, height: 56, borderRadius: "14px", bgcolor: "#f8fafc", border: "1.5px dashed #d1d5db", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <AssignmentTurnedInIcon sx={{ fontSize: "1.5rem", color: "#d1d5db" }} />
    </Box>
    <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#374151" }}>{message || "No assessments found"}</Typography>
    {onAssign && (
      <Button variant="outlined" onClick={onAssign}
        sx={{ mt: 0.5, textTransform: "none", fontWeight: 600, fontSize: "0.82rem", borderRadius: "8px", px: 2.5, py: 0.75, borderColor: "#3b82f6", color: "#3b82f6", "&:hover": { bgcolor: "#eff6ff" } }}>
        + Assign Assessment
      </Button>
    )}
  </Box>
);

// Main Component

const AssessmentDetails = ({ hideNavbar = false, hideOuterWrapper = false, mainSection: mainSectionProp, setMainSection: setMainSectionProp }) => {
  const [appointments, setAppointments]             = useState([]);
  const [user, setUser]                             = useState(null);
  const [assignOpen, setAssignOpen]                 = useState(false);
  const [selectedPatient, setSelectedPatient]       = useState("");
  const [selectedTypes, setSelectedTypes]           = useState([]);
  const [assessments, setAssessments]               = useState([]);
  const [patientAssessments, setPatientAssessments] = useState([]);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [assigning, setAssigning]                   = useState(false);
  const [search, setSearch]                         = useState("");
  const [activeTab, setActiveTab]                   = useState("all");
  const [mainSectionInternal, setMainSectionInternal]   = useState("assigned");
  const mainSection    = mainSectionProp    !== undefined ? mainSectionProp    : mainSectionInternal;
  const setMainSection = setMainSectionProp !== undefined ? setMainSectionProp : setMainSectionInternal;

  useEffect(() => {
    const s = localStorage.getItem("user");
    if (s) setUser(JSON.parse(s));
  }, []);

  useEffect(() => {
    if (!user) return;
    const doctorId = user?.id || user?.user_id || user?.doctor_id || user?._id;
    if (!doctorId) return;
    axios.get("/api/v1/appointments/doctor/" + doctorId + "/today",
      { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
    ).then((r) => {
      const all = Array.isArray(r.data) ? r.data : Array.isArray(r.data?.data) ? r.data.data : [];
      const active = all.filter((a) => a.status !== "cancelled");
      setAppointments([...new Map(active.map((a) => [a.customer_id, a])).values()]);
    }).catch(() => setAppointments([]));
  }, [user]);

  useEffect(() => {
    getAssessments().then((d) => setAssessments(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const fetchAssignedAssessments = async () => {
    const doctorId = user?.id || user?.user_id || user?.doctor_id || user?._id;
    if (!doctorId) return;
    setLoadingAssessments(true);
    try {
      const data = await getAssignedAssessments(doctorId);
      const list = Array.isArray(data) ? data : [];
      setPatientAssessments(list.map((assigned) => {
        const masterRecord   = assessments.find((a) => (a.assessment_key ?? a.id) === assigned.assessment_key);
        const assessmentName = masterRecord?.name || masterRecord?.assessment_name || assigned.assessment_key || "Unknown Assessment";
        const appt           = appointments.find((a) => a.customer_id === assigned.customer_id);
        const patientName    = assigned.customer_name || appt?.customer_name || ("Patient #" + assigned.customer_id);
        return {
          id:            assigned.id,
          icon:          getCategoryIcon(assessmentName),
          assessmentName,
          patientName,
          date:          assigned.assigned_at ? new Date(assigned.assigned_at).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "UTC" }) : "—",
          assignedAt:    assigned.assigned_at ? new Date(assigned.assigned_at) : null,
          category:      getCategory(assessmentName),
          status:        assigned.status === "completed" ? "completed" : "not-started",
          customerId:    assigned.customer_id,
          appointmentId: assigned.appointment_id,
          assessmentKey: assigned.assessment_key,
          obtainedScore: assigned.obtained_score ?? null,
          totalScore:    assigned.total_score ?? null,
        };
      }));
    } catch (err) {
      console.error("Error fetching assigned assessments:", err);
      setPatientAssessments([]);
    } finally {
      setLoadingAssessments(false);
    }
  };

  useEffect(() => { if (user) fetchAssignedAssessments(); }, [user, assessments, appointments]);

  const is72hLocked = (a) => {
    if (!a.assignedAt) return false;
    return (Date.now() - a.assignedAt.getTime()) / 3_600_000 >= 72;
  };

  const totalCount     = patientAssessments.length;
  const pendingCount   = patientAssessments.filter((a) => a.status !== "completed").length;
  const completedCount = patientAssessments.filter((a) => a.status === "completed").length;

  const displayed = useMemo(() => {
    let list = [...patientAssessments];
    if (activeTab === "pending")   list = list.filter((a) => a.status !== "completed");
    if (activeTab === "completed") list = list.filter((a) => a.status === "completed");
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((a) =>
        a.assessmentName.toLowerCase().includes(q) ||
        a.patientName.toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q)
      );
    }
    return list;
  }, [patientAssessments, activeTab, search]);

  const groupedAssessments = assessments.reduce((acc, a) => {
    const name = a.name || a.assessment_name || "";
    const cat  = getCategory(name);
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(a);
    return acc;
  }, { anxiety: [], depression: [], other: [] });

  const handleCloseDialog = () => {
    setAssignOpen(false);
    setSelectedPatient("");
    setSelectedTypes([]);
  };

  const handleAssign = async () => {
    if (!selectedPatient || selectedTypes.length === 0) {
      alert("Please select a patient and at least one assessment type.");
      return;
    }
    const selectedAppointment = appointments.find((apt) => apt.customer_id === selectedPatient);
    if (!selectedAppointment) {
      alert("Could not find appointment for selected patient.");
      return;
    }
    setAssigning(true);
    try {
      for (const assessmentKey of selectedTypes) {
        await assignAssessment({
          customer_id:    selectedPatient,
          appointment_id: selectedAppointment.id,
          doctor_id:      user?.id || user?.user_id || user?.doctor_id || user?._id,
          assessment_key: assessmentKey,
        });
      }
      handleCloseDialog();
      await fetchAssignedAssessments();
    } catch (err) {
      const msg = err?.response?.data?.error || "";
      if (err?.response?.status === 409 || msg.toLowerCase().includes("already")) {
        alert("This assessment is already assigned to the patient.");
      } else {
        alert("Failed to assign assessment. Please try again.");
      }
      console.error("Error assigning assessment:", err);
    } finally {
      setAssigning(false);
    }
  };

  const assignDialog = (
    <Dialog open={assignOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", borderBottom: "1px solid #e9ecef", py: 2, px: 3, bgcolor: "#fafafa" }}>
        Assign Assessment
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <FormControl fullWidth margin="normal">
          <InputLabel>Patient *</InputLabel>
          <Select value={selectedPatient} label="Patient *" onChange={(e) => { setSelectedPatient(e.target.value); setSelectedTypes([]); }}
            sx={{ borderRadius: "8px", fontSize: "0.875rem" }}>
            {appointments.length === 0
              ? <MenuItem disabled><em>No patients found</em></MenuItem>
              : appointments.map((apt) => (
                  <MenuItem key={apt.customer_id} value={apt.customer_id} sx={{ fontSize: "0.875rem", display: "flex", alignItems: "center", gap: 1 }}>
                    <Box component="span" sx={{ fontFamily: "monospace", fontSize: "0.75rem", color: "#9ca3af", bgcolor: "#f3f4f6", px: 0.75, py: 0.2, borderRadius: "4px", flexShrink: 0 }}>#{apt.customer_id}</Box>
                    <Box component="span">{apt.customer_name}</Box>
                  </MenuItem>
                ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel>Assessment Type *</InputLabel>
          <Select multiple value={selectedTypes} label="Assessment Type *"
            onChange={(e) => setSelectedTypes(e.target.value)}
            sx={{ borderRadius: "8px", fontSize: "0.875rem" }}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((key) => {
                  const found = assessments.find((a) => (a.assessment_key ?? a.id) === key);
                  const name  = found?.name || found?.assessment_name || key;
                  const short = found?.short_name || found?.shortName || "";
                  const displayLabel = getCategoryIcon(name) + " " + (short || name);
                  return (
                    <Chip key={key} label={displayLabel} size="small"
                      onDelete={() => setSelectedTypes((prev) => prev.filter((k) => k !== key))}
                      onMouseDown={(e) => e.stopPropagation()}
                      sx={{ fontSize: "0.72rem", fontWeight: 500, height: 24, bgcolor: "#eff6ff", color: "#1e40af", border: "1px solid #bfdbfe", borderRadius: "6px" }} />
                  );
                })}
              </Box>
            )}
            MenuProps={{ PaperProps: { sx: { maxHeight: 400, borderRadius: "10px" } } }}>
            {["anxiety", "depression", "other"].map((cat) => {
              const items = groupedAssessments[cat];
              if (!items || items.length === 0) return null;
              return [
                <MenuItem key={"header-" + cat} disabled
                  sx={{ fontStyle: "italic", color: "#9ca3af !important", fontSize: "0.78rem", py: 0.5, minHeight: "auto", opacity: "1 !important" }}>
                  {categoryLabels[cat]}
                </MenuItem>,
                ...items.map((assessment) => {
                  const key        = assessment.assessment_key ?? assessment.id;
                  const name       = assessment.name || assessment.assessment_name || "";
                  const shortName  = assessment.short_name || assessment.shortName || "";
                  const icon       = getCategoryIcon(name);
                  const isSelected = selectedTypes.includes(key);
  const isAlreadyAssigned = patientAssessments.some(
    pa =>
      pa.customerId === selectedPatient &&
      pa.assessmentKey === key &&
      pa.status !== "completed"
  );
  return (
    <MenuItem key={key} value={key} disabled={isAlreadyAssigned}
      sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.875rem", py: 1,
        bgcolor: isSelected ? "#eff6ff" : "transparent",
        "&:hover": { bgcolor: isSelected ? "#dbeafe" : "#f9fafb" },
                        "&.Mui-selected": { bgcolor: "#eff6ff" },
                        "&.Mui-selected:hover": { bgcolor: "#dbeafe" },
                        "&.Mui-disabled": { opacity: 0.5 } }}>
                      <Box component="span" sx={{ fontSize: "1rem", lineHeight: 1 }}>{icon}</Box>
                      <Box component="span" sx={{ flex: 1, color: "#1a1a1a" }}>{name}</Box>
                      {shortName && <Box component="span" sx={{ color: "#6b7280", fontSize: "0.78rem" }}>({shortName})</Box>}
                      {isAlreadyAssigned
                        ? <Box component="span" sx={{ color: "#22c55e", fontSize: "0.72rem", ml: 0.5, fontWeight: 600 }}>✓ assigned</Box>
                        : isSelected && <Box component="span" sx={{ color: "#3b82f6", fontWeight: 700, fontSize: "0.78rem", ml: 0.5 }}>✓</Box>}
                    </MenuItem>
                  );
                }),
              ];
            })}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, borderTop: "1px solid #e9ecef", bgcolor: "#fafafa", gap: 1 }}>
        <Button onClick={handleCloseDialog}
          sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.82rem", borderRadius: "8px", px: 2.25, color: "#374151", border: "1px solid #d1d5db", "&:hover": { bgcolor: "#f3f4f6" } }}>
          Cancel
        </Button>
        <Button onClick={handleAssign} variant="contained" disabled={assigning}
          startIcon={assigning ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.82rem", borderRadius: "8px", px: 2.5, bgcolor: "#3b82f6", boxShadow: "none", "&:hover": { bgcolor: "#2563eb", boxShadow: "none" } }}>
          {assigning ? "Assigning…" : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, maxWidth: 1400, mx: "auto" }}>

      {/* ── Header ── */}
      {mainSection !== "library" && (
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1.15rem", color: "#111827" }}>Assessments</Typography>
          <Typography sx={{ fontSize: "0.82rem", color: "#6b7280", mt: 0.3 }}>Track and manage patient assessments</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {/* Only show the section switcher when not controlled externally (standalone page) */}
          {mainSectionProp === undefined && (
            <Box sx={{ display: "flex", bgcolor: "#f1f5f9", borderRadius: "10px", p: 0.4, gap: 0.4 }}>
              <Button size="small" onClick={() => setMainSection("assigned")}
                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.78rem", borderRadius: "8px", px: 1.5, py: 0.5, minWidth: 0, bgcolor: mainSection === "assigned" ? "#fff" : "transparent", color: mainSection === "assigned" ? "#111827" : "#6b7280", boxShadow: mainSection === "assigned" ? "0 1px 3px rgba(0,0,0,0.1)" : "none", "&:hover": { bgcolor: mainSection === "assigned" ? "#fff" : "#e2e8f0" } }}>
                Assigned
              </Button>
              <Button size="small" onClick={() => setMainSection("library")}
                startIcon={<LibraryBooksOutlinedIcon sx={{ fontSize: "0.85rem !important" }} />}
                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.78rem", borderRadius: "8px", px: 1.5, py: 0.5, minWidth: 0, bgcolor: mainSection === "library" ? "#fff" : "transparent", color: mainSection === "library" ? "#111827" : "#6b7280", boxShadow: mainSection === "library" ? "0 1px 3px rgba(0,0,0,0.1)" : "none", "&:hover": { bgcolor: mainSection === "library" ? "#fff" : "#e2e8f0" } }}>
                Library
              </Button>
            </Box>
          )}
          {mainSection === "assigned" && (
            <Button variant="contained" onClick={() => setAssignOpen(true)}
              sx={{ bgcolor: "#3b82f6", px: 2.5, py: 1, borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: "0.875rem", boxShadow: "none", "&:hover": { bgcolor: "#2563eb", boxShadow: "none" } }}>
              + Assign
            </Button>
          )}
        </Box>
      </Box>
      )}

      {mainSection === "library" ? <LibraryView /> : <>

      {/* ── Stats ── */}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        <StatCard label="Total" count={totalCount} color="#3b82f6" bg="#eff6ff" icon={<AssignmentTurnedInIcon sx={{ fontSize: "1.1rem" }} />} />
        <StatCard label="Pending" count={pendingCount} color="#f97316" bg="#fff7ed" icon={<ScheduleIcon sx={{ fontSize: "1.1rem" }} />} />
        <StatCard label="Completed" count={completedCount} color="#22c55e" bg="#f0fdf4" icon={<CheckCircleIcon sx={{ fontSize: "1.1rem" }} />} />
      </Box>

      {/* ── Filter bar ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
          sx={{ minHeight: 36, "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.82rem", minHeight: 36, py: 0.5, color: "#6b7280", px: 2 }, "& .Mui-selected": { color: "#2563eb !important" }, "& .MuiTabs-indicator": { bgcolor: "#2563eb", height: 2 } }}>
          <Tab label="All" value="all" />
          <Tab label={"Pending" + (pendingCount > 0 ? " (" + pendingCount + ")" : "")} value="pending" />
          <Tab label={"Completed" + (completedCount > 0 ? " (" + completedCount + ")" : "")} value="completed" />
        </Tabs>
        <TextField variant="outlined" size="small" placeholder="Search patient or assessment..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#9ca3af", fontSize: "1rem" }} /></InputAdornment>,
            sx: { borderRadius: "10px", bgcolor: "#fff", fontSize: "0.82rem", "& fieldset": { borderColor: "#e2e8f0" }, "&:hover fieldset": { borderColor: "#cbd5e1" }, "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: "1.5px" } },
          }}
          sx={{ width: 260 }} />
      </Box>

      {/* ── List ── */}
      {loadingAssessments ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: "#3b82f6" }} />
        </Box>
      ) : displayed.length === 0 ? (
        <Box sx={{ border: "1px solid #e9ecef", borderRadius: "14px", bgcolor: "#fff" }}>
          <EmptyState
            message={
              search.trim() ? "No assessments match your search" :
              activeTab === "pending" ? "No pending assessments" :
              activeTab === "completed" ? "No completed assessments" :
              "No assessments assigned yet"
            }
            onAssign={totalCount === 0 ? () => setAssignOpen(true) : null}
          />
        </Box>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" }, gap: 1.5 }}>
          {displayed.map((a) => <AssessmentCard key={a.id} a={a} locked={is72hLocked(a)} />)}
        </Box>
      )}

      </> }
    </Box>
  );

  if (hideOuterWrapper) {
    return (
      <>
        {!hideNavbar && <Navbar />}
        {content}
        {assignDialog}
      </>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f4f6f9" }}>
      {!hideNavbar && <Navbar />}
      <Box sx={{ mx: { xs: 2, md: 4 }, py: 3 }}>{content}</Box>
      {assignDialog}
    </Box>
  );
};

export default AssessmentDetails;
